import { useEffect, useMemo, useState } from 'react';

export default function Photos({ photos, title }) {
  const [currentImage, setCurrentImage] = useState(0);
  const [viewerIsOpen, setViewerIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Prefer 1080 or 720 versions when available (Airtable-style), with sensible fallbacks
  const getBestViewUrl = photo => {
    return (
      photo.url1080 ||
      photo.url_1080 ||
      (photo.thumbnails && photo.thumbnails.large && photo.thumbnails.large.url) ||
      (photo.thumbnails && photo.thumbnails.full && photo.thumbnails.full.url) ||
      photo.url720 ||
      photo.url_720 ||
      photo.url ||
      (photo.thumbnails && photo.thumbnails.small && photo.thumbnails.small.url) ||
      photo.originalUrl ||
      null
    );
  };

  const getThumbUrl = photo => {
    return (
      photo.thumbnailUrl ||
      (photo.thumbnails && photo.thumbnails.small && photo.thumbnails.small.url) ||
      (photo.thumbnails && photo.thumbnails.large && photo.thumbnails.large.url) ||
      photo.url ||
      null
    );
  };

  // Only keep photos that have a viewable URL (1080/720 preferred)
  const displayPhotos = useMemo(() => {
    return (photos || [])
      .map(p => ({ thumb: getThumbUrl(p), viewUrl: getBestViewUrl(p) }))
      .filter(p => Boolean(p.viewUrl));
  }, [photos]);

  const openLightbox = index => {
    setCurrentImage(index);
    setViewerIsOpen(true);
    setIsLoading(true);
  };

  const closeLightbox = () => {
    setCurrentImage(0);
    setViewerIsOpen(false);
  };

  const showPrev = e => {
    if (e) e.stopPropagation();
    setCurrentImage(i => (i > 0 ? i - 1 : 0));
  };

  const showNext = e => {
    if (e) e.stopPropagation();
    setCurrentImage(i =>
      i < displayPhotos.length - 1 ? i + 1 : displayPhotos.length - 1
    );
  };

  useEffect(() => {
    if (viewerIsOpen) {
      setIsLoading(true);
    }
  }, [currentImage, viewerIsOpen]);

  return (
    <div className="photos-container">
      {displayPhotos.map((photo, index) => {
        return (
          <div
            key={index}
            className="img-container"
            onClick={() => openLightbox(index)}
          >
            {photo.thumb ? (
              <img src={photo.thumb} alt={title} height="32px" />
            ) : null}
          </div>
        );
      })}
      {viewerIsOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closeLightbox}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <button
            onClick={e => {
              e.stopPropagation();
              closeLightbox();
            }}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 12,
              right: 16,
              fontSize: 24,
              background: 'transparent',
              color: '#fff',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            ×
          </button>

          {displayPhotos.length > 1 && currentImage > 0 ? (
            <button
              onClick={showPrev}
              aria-label="Previous"
              style={{
                position: 'absolute',
                left: 16,
                color: '#fff',
                fontSize: 32,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              ‹
            </button>
          ) : null}

          {displayPhotos[currentImage] ? (
            <img
              src={displayPhotos[currentImage].viewUrl}
              alt={title}
              onClick={e => e.stopPropagation()}
              onLoad={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
              className={isLoading ? 'img-loading' : undefined}
              style={{
                maxWidth: '90vw',
                maxHeight: '85vh',
                objectFit: 'contain',
                boxShadow: '0 4px 24px rgba(0,0,0,0.5)'
              }}
            />
          ) : null}

          {displayPhotos.length > 1 && currentImage < displayPhotos.length - 1 ? (
            <button
              onClick={showNext}
              aria-label="Next"
              style={{
                position: 'absolute',
                right: 16,
                color: '#fff',
                fontSize: 32,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              ›
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
